/**
 * Shared utilities for safe .NET interop invocation.
 * Handles disposal and error scenarios gracefully.
 */

'use strict';

import type { DotNetObjectReference } from '../types.js';

/**
 * Safely invoke a .NET interop method without throwing.
 * Returns undefined if invocation fails or helper is disposed.
 * 
 * @param helper - DotNet object reference
 * @param methodName - Method name to invoke
 * @param args - Arguments to pass
 */
export async function safeInvoke(
    helper: DotNetObjectReference | null | undefined,
    methodName: string,
    ...args: any[]
): Promise<any> {
    if (!helper || typeof helper.invokeMethodAsync !== 'function') {
        return undefined;
    }

    try {
        return await helper.invokeMethodAsync(methodName, ...args);
    } catch {
        // Swallow if the .NET object got disposed or interop failed
        return undefined;
    }
}

/**
 * Safely invoke a .NET interop method synchronously (fire-and-forget).
 * Does not wait for the promise to resolve.
 * 
 * @param helper - DotNet object reference
 * @param methodName - Method name to invoke
 * @param args - Arguments to pass
 */
export function safeInvokeSync(
    helper: DotNetObjectReference | null | undefined,
    methodName: string,
    ...args: any[]
): void {
    if (!helper || typeof helper.invokeMethodAsync !== 'function') {
        return;
    }

    try {
        void helper.invokeMethodAsync(methodName, ...args);
    } catch {
        // Swallow errors for fire-and-forget scenarios
    }
}
